/**
 * GM cart drawer — limpar sacola + cupom + mini drawer de frete (spec 0054 / 0057).
 * Cupom e CEP: inputs compactos sempre visíveis no footer.
 * CEP PDP → sacola → checkout: spec 0116 / ADR-088.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const STORAGE_CEP = 'gm_cart_cep';
const STORAGE_RATE = 'gm_cart_shipping_rate';
const DEBOUNCE_MS = 400;

/** @type {ReturnType<typeof setTimeout> | null} */
let cepDebounceTimer = null;
/** @type {number} */
let estimateSeq = 0;

function moneyBRL(cents) {
  return (Number(cents) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Preço de exibição: se grátis com compare (tema), "~~R$ X~~ Grátis".
 * Aceita `gm_compare_at_price` (tema) ou `compare_at_price` / `original_price` (API).
 * @param {object} rate
 * @param {string} freeLabel
 * @returns {string} HTML seguro
 */
function formatRatePriceHtml(rate, freeLabel) {
  const priceNum = Number(rate && rate.price);
  const compareRaw =
    rate &&
    (rate.gm_compare_at_price ?? rate.compare_at_price ?? rate.original_price);
  const compareNum = Number(compareRaw);
  const hasCompare =
    Number.isFinite(compareNum) && compareNum > 0 && priceNum === 0;

  if (priceNum === 0) {
    const free = `<span class="gm-shipping-price-free">${escapeHtml(freeLabel)}</span>`;
    if (!hasCompare) return free;
    return (
      `<s class="gm-shipping-price-was">${escapeHtml(moneyBRL(Math.round(compareNum * 100)))}</s>` +
      ` ${free}`
    );
  }
  return escapeHtml(moneyBRL(Math.round(priceNum * 100)));
}

function formatCep(value) {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 8);
  if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return digits;
}

/**
 * @param {string} key
 * @returns {string | null}
 */
function readStorage(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function writeStorage(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (_) {
    /* ignore */
  }
}

function readCepDigits() {
  if (window.GMFreteExpresso && typeof window.GMFreteExpresso.readCep === 'function') {
    return window.GMFreteExpresso.readCep() || '';
  }
  const raw = readStorage(STORAGE_CEP);
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length === 8 ? digits : '';
}

/**
 * @param {string} digits
 */
function writeCepDigits(digits) {
  if (window.GMFreteExpresso && typeof window.GMFreteExpresso.writeCep === 'function') {
    window.GMFreteExpresso.writeCep(digits);
    return;
  }
  const next = String(digits || '').replace(/\D/g, '').slice(0, 8);
  try {
    if (next.length === 8) sessionStorage.setItem(STORAGE_CEP, next);
    else sessionStorage.removeItem(STORAGE_CEP);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @returns {{ name: string, price: string, code?: string, deliveryDays?: number | null, label: string } | null}
 */
function readStoredRate() {
  const raw = readStorage(STORAGE_RATE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.label) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/**
 * @param {Element} from
 * @returns {HTMLElement | null}
 */
function getShippingHost(from) {
  const main = from.closest('.gm-cart-drawer__main');
  if (main instanceof HTMLElement) return main;
  const host = from.closest('[data-gm-shipping-host]');
  if (host instanceof HTMLElement) return host;
  return null;
}

/**
 * @param {HTMLElement} host
 */
function getSheetEls(host) {
  const sheet = host.querySelector('[data-gm-shipping-sheet]');
  const backdrop = host.querySelector('[data-gm-shipping-sheet-backdrop]');
  const input = host.querySelector('[data-gm-cart-cep-input]');
  const status = host.querySelector('[data-gm-shipping-status]');
  const rates = host.querySelector('[data-gm-shipping-rates]');
  const shippingRow = host.querySelector('[data-gm-cart-shipping]');
  const selectedBtn = shippingRow?.querySelector('[data-gm-cart-shipping-selected]');
  const totalEl = host.querySelector('[data-cart-subtotal-cents]');
  return { sheet, backdrop, input, status, rates, shippingRow, selectedBtn, totalEl };
}

/**
 * @param {HTMLElement | null} statusEl
 * @param {string} message
 * @param {boolean} [show]
 */
function setStatus(statusEl, message, show = true) {
  if (!(statusEl instanceof HTMLElement)) return;
  statusEl.textContent = message;
  statusEl.hidden = !show || !message;
}

/**
 * @param {unknown} rate
 * @param {HTMLElement} sheet
 * @returns {string}
 */
/**
 * @param {unknown} rate
 * @param {HTMLElement} sheet
 * @returns {string} HTML
 */
function formatRateSummary(rate, sheet) {
  const freeLabel = sheet.dataset.msgFree || 'Grátis';
  const untilTpl = sheet.dataset.msgUntil || '(até __DAYS__ dias)';
  const price = formatRatePriceHtml(/** @type {object} */ (rate), freeLabel);

  const days = getDeliveryDays(rate);
  if (days == null) return price;
  return `${price} ${escapeHtml(untilTpl.replace('__DAYS__', String(days)))}`;
}

/**
 * @param {unknown} rate
 * @param {HTMLElement} sheet
 * @returns {string} HTML
 */
function formatRateOptionLabel(rate, sheet) {
  const r = /** @type {{ presentment_name?: string, name?: string, price?: string, gm_eta?: string }} */ (
    rate
  );
  const name = escapeHtml(String(r.presentment_name || r.name || 'Frete'));
  const freeLabel = sheet.dataset.msgFree || 'Grátis';
  const price = formatRatePriceHtml(r, freeLabel);
  if (r.gm_eta) return `${price} - ${name} ${escapeHtml(String(r.gm_eta))}`;
  const days = getDeliveryDays(rate);
  const untilTpl = sheet.dataset.msgUntil || '(até __DAYS__ dias)';
  if (days == null) return `${price} - ${name}`;
  return `${price} - ${name} ${escapeHtml(untilTpl.replace('__DAYS__', String(days)))}`;
}

/**
 * @param {unknown} rate
 * @returns {number | null}
 */
function getDeliveryDays(rate) {
  const r = /** @type {{ delivery_days?: number[], delivery_range?: string[] }} */ (rate);
  if (Array.isArray(r.delivery_days) && r.delivery_days.length) {
    const max = Math.max(...r.delivery_days.map(Number).filter((n) => !Number.isNaN(n)));
    return Number.isFinite(max) ? max : null;
  }
  if (Array.isArray(r.delivery_range) && r.delivery_range.length >= 2) {
    const end = new Date(r.delivery_range[1]);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (!Number.isNaN(end.getTime())) {
      const diff = Math.ceil((end.getTime() - start.getTime()) / 86400000);
      return diff > 0 ? diff : null;
    }
  }
  return null;
}

/**
 * @param {string} digits
 * @returns {Promise<{ uf: string, localidade: string } | null>}
 */
async function lookupCepGeo(digits) {
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.erro) return null;
    const uf = typeof data?.uf === 'string' && data.uf ? data.uf : '';
    const localidade =
      typeof data?.localidade === 'string' && data.localidade ? data.localidade : '';
    if (
      (uf || localidade) &&
      window.GMFreteExpresso &&
      typeof window.GMFreteExpresso.writeCepGeo === 'function'
    ) {
      window.GMFreteExpresso.writeCepGeo({ uf, localidade });
    }
    return uf || localidade ? { uf, localidade } : null;
  } catch (_) {
    return null;
  }
}

/**
 * @param {unknown} data
 * @returns {boolean}
 */
function isEmptyCartShippingError(data) {
  const err = data && typeof data === 'object' ? data.error : null;
  const text = Array.isArray(err) ? err.join(' ') : String(err || '');
  return /n[aã]o exige frete|does not require shipping/i.test(text);
}

/**
 * @param {string} digits
 * @param {{ uf?: string, localidade?: string, city?: string } | null} geo
 * @returns {Promise<object[]>}
 */
async function fetchShippingRates(digits, geo) {
  const params =
    window.GMFreteExpresso &&
    typeof window.GMFreteExpresso.shippingAddressParams === 'function'
      ? window.GMFreteExpresso.shippingAddressParams(digits, geo)
      : new URLSearchParams({
          'shipping_address[zip]': digits,
          'shipping_address[country]': 'Brazil',
          include_carrier_rates: 'true',
        });
  if (
    !(window.GMFreteExpresso && typeof window.GMFreteExpresso.shippingAddressParams === 'function')
  ) {
    if (geo?.uf) params.set('shipping_address[province]', geo.uf);
    if (geo?.localidade || geo?.city) {
      params.set('shipping_address[city]', String(geo.localidade || geo.city));
    }
  }

  const cartUrl = Theme.routes.cart_url;
  const prepareRes = await fetch(
    `${cartUrl}/prepare_shipping_rates.json?${params}`,
    { method: 'POST', headers: { Accept: 'application/json' } }
  );
  if (!prepareRes.ok) throw new Error('prepare_shipping_rates failed');

  const maxAttempts = 40;
  const pollMs = 250;
  let emptyCartPolls = 0;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const asyncRes = await fetch(
      `${cartUrl}/async_shipping_rates.json?${params}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!asyncRes.ok) throw new Error('async_shipping_rates failed');
    const data = await asyncRes.json();
    if (data == null) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      continue;
    }
    if (isEmptyCartShippingError(data)) {
      emptyCartPolls += 1;
      if (emptyCartPolls >= 3) return [];
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      continue;
    }
    if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
    if (data.error) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      continue;
    }
    return [];
  }

  const res = await fetch(`${cartUrl}/shipping_rates.json?${params}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('shipping_rates failed');
  const data = await res.json();
  if (isEmptyCartShippingError(data)) return [];
  return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
}

/**
 * @param {HTMLElement} host
 * @param {object[]} rateList
 * @param {string | null} selectedCode
 */
function renderRates(host, rateList, selectedCode) {
  const { sheet, rates, status } = getSheetEls(host);
  if (!(rates instanceof HTMLElement) || !(sheet instanceof HTMLElement)) return;

  rates.replaceChildren();

  if (!rateList.length) {
    setStatus(status instanceof HTMLElement ? status : null, sheet.dataset.msgError || '', true);
    return;
  }

  setStatus(status instanceof HTMLElement ? status : null, '', false);

  rateList.forEach((rate, index) => {
    const code = String(rate.code || rate.name || index);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gm-cart-shipping-sheet__rate cursor-pointer';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('data-gm-shipping-rate', code);
    const isSelected = selectedCode != null && selectedCode === code;
    btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    if (isSelected) btn.classList.add('is-selected');

    const radio = document.createElement('span');
    radio.className = 'gm-cart-shipping-sheet__rate-radio';
    radio.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.className = 'gm-cart-shipping-sheet__rate-label';
    text.innerHTML = formatRateOptionLabel(rate, sheet);

    btn.append(radio, text);
    btn.addEventListener('click', () => {
      selectRate(host, rate, code);
    });

    rates.appendChild(btn);
  });
}

/**
 * @param {HTMLElement} host
 * @returns {number}
 */
function getCartTotalCents(host) {
  const totalEl = host.querySelector('[data-cart-subtotal-cents]');
  if (totalEl instanceof HTMLElement) {
    const n = Number(totalEl.getAttribute('data-cart-subtotal-cents') || '0');
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/**
 * @param {HTMLElement} host
 * @returns {number}
 */
function getFreeShipThresholdCents(host) {
  const el = host.querySelector('[data-gm-free-ship]');
  if (el instanceof HTMLElement) {
    const n = Number(el.getAttribute('data-threshold') || '');
    if (Number.isFinite(n) && n > 0) return n;
  }
  return (
    (typeof window !== 'undefined' &&
      window.GMFreteExpresso &&
      window.GMFreteExpresso.FREE_SHIP_THRESHOLD_CENTS) ||
    150000
  );
}

/**
 * @param {string} code
 * @returns {boolean}
 */
function isExpressoRateCode(code) {
  return String(code || '').startsWith('gm-frete-expresso-');
}

/**
 * @param {HTMLElement} host
 * @param {object} rate
 * @param {string} code
 * @returns {{ name: string, price: string, code: string, deliveryDays: number | null, label: string }}
 */
function buildStoredRate(host, rate, code) {
  const { sheet } = getSheetEls(host);
  const label =
    sheet instanceof HTMLElement ? formatRateSummary(rate, sheet) : String(rate.price || '');
  const compare =
    rate.gm_compare_at_price ?? rate.compare_at_price ?? rate.original_price;
  return {
    name: String(rate.presentment_name || rate.name || ''),
    price: String(rate.price ?? '0'),
    code,
    deliveryDays: getDeliveryDays(rate),
    label,
    compare_at_price:
      compare != null && Number(compare) > 0 ? String(compare) : undefined,
  };
}

/**
 * Se a escolha guardada é Frete Expresso, reaplica preço da planilha.
 * Frete grátis (≥ R$ 1.500) é recalculado na próxima cotação (mais barata paga).
 * @param {HTMLElement} host
 * @param {{ name: string, price: string, code?: string, deliveryDays?: number | null, label: string } | null} stored
 * @returns {{ name: string, price: string, code?: string, deliveryDays?: number | null, label: string } | null}
 */
function refreshExpressoStoredRate(host, stored) {
  if (!stored || !isExpressoRateCode(stored.code)) return stored;
  if (
    typeof window === 'undefined' ||
    !window.GMFreteExpresso ||
    typeof window.GMFreteExpresso.match !== 'function'
  ) {
    return stored;
  }

  const cep = readCepDigits();
  if (!cep || cep.replace(/\D/g, '').length !== 8) return stored;

  // Só reaplica planilha. Frete grátis (≥ threshold) exige merge com carriers
  // (mais barata paga) — feito de novo na próxima cotação do sheet.
  const fresh = window.GMFreteExpresso.match(cep);
  if (!fresh) return stored;

  const next = buildStoredRate(host, fresh, String(fresh.code || stored.code));
  if (next.price !== stored.price || next.label !== stored.label) {
    writeStorage(STORAGE_RATE, JSON.stringify(next));
  }
  return next;
}

/**
 * @param {HTMLElement} host
 * @param {object} rate
 * @param {string} code
 */
function selectRate(host, rate, code) {
  const payload = buildStoredRate(host, rate, code);
  writeStorage(STORAGE_RATE, JSON.stringify(payload));

  const cepInput = host.querySelector('[data-gm-cart-cep-input]');
  if (cepInput instanceof HTMLInputElement) {
    writeCepDigits(cepInput.value.replace(/\D/g, ''));
  }

  applyShippingSummary(host, payload);
  closeShippingSheet(host);
}

/**
 * @param {HTMLElement} host
 * @param {{ label: string, price: string } | null} stored
 */
function applyShippingSummary(host, stored) {
  const { selectedBtn, totalEl } = getSheetEls(host);

  if (stored && selectedBtn instanceof HTMLElement) {
    selectedBtn.hidden = false;
    // label pode conter <s> do preço riscado (montado por nós)
    selectedBtn.innerHTML = stored.label || '';
  } else if (selectedBtn instanceof HTMLElement) {
    selectedBtn.hidden = true;
    selectedBtn.textContent = '';
  }

  if (!(totalEl instanceof HTMLElement)) return;

  const subtotalCents = Number(totalEl.getAttribute('data-cart-subtotal-cents') || '0');
  if (!stored) {
    totalEl.textContent = moneyBRL(subtotalCents);
    if ('value' in totalEl) {
      /** @type {HTMLElement & { value?: string }} */ (totalEl).value = moneyBRL(subtotalCents);
    }
    return;
  }

  const shipCents = Math.round(Number(stored.price) * 100);
  const estimated = moneyBRL(subtotalCents + (Number.isFinite(shipCents) ? shipCents : 0));
  totalEl.textContent = estimated;
  if ('value' in totalEl) {
    /** @type {HTMLElement & { value?: string }} */ (totalEl).value = estimated;
  }
}

/**
 * @param {HTMLElement} host
 */
function hydrateShippingSummary(host) {
  const cep = readCepDigits();
  const { input } = getSheetEls(host);
  if (cep && input instanceof HTMLInputElement && document.activeElement !== input) {
    input.value = formatCep(cep);
  }
  let stored = readStoredRate();
  stored = refreshExpressoStoredRate(host, stored);
  applyShippingSummary(host, stored);
}

/**
 * @param {HTMLElement} host
 * @returns {{ el: Element, top: number }[]}
 */
function freezeHostScroll(host) {
  /** @type {{ el: Element, top: number }[]} */
  const saved = [];
  host.querySelectorAll('.cart-drawer__items, .gm-cart-drawer__content').forEach((el) => {
    saved.push({ el, top: el.scrollTop });
  });
  return saved;
}

/**
 * @param {{ el: Element, top: number }[]} saved
 */
function restoreHostScroll(saved) {
  saved.forEach(({ el, top }) => {
    el.scrollTop = top;
  });
}

/**
 * @param {HTMLElement} host
 */
function openShippingSheet(host) {
  const { sheet, backdrop, input } = getSheetEls(host);
  if (!(sheet instanceof HTMLElement) || !(backdrop instanceof HTMLElement)) return;

  if (input instanceof HTMLInputElement) input.blur();

  if (host.classList.contains('is-shipping-sheet-open')) {
    const cep = readCepDigits();
    if (input instanceof HTMLInputElement) {
      if (cep && document.activeElement !== input) input.value = formatCep(cep);
      if (input.value.replace(/\D/g, '').length === 8) {
        estimateShipping(host, input.value);
      }
    }
    return;
  }

  const savedScroll = freezeHostScroll(host);

  host.classList.add('is-shipping-sheet-open');
  backdrop.hidden = false;
  sheet.hidden = false;
  sheet.classList.remove('is-open');
  // Two frames so transform transition runs after display:flex paints.
  requestAnimationFrame(() => {
    sheet.classList.add('is-open');
    restoreHostScroll(savedScroll);
  });

  const cep = readCepDigits();
  if (input instanceof HTMLInputElement) {
    if (cep && document.activeElement !== input) input.value = formatCep(cep);
    restoreHostScroll(savedScroll);
    requestAnimationFrame(() => restoreHostScroll(savedScroll));
    if (input.value.replace(/\D/g, '').length === 8) {
      estimateShipping(host, input.value);
    }
  }
}

/**
 * @param {HTMLElement} host
 */
function closeShippingSheet(host) {
  const { sheet, backdrop } = getSheetEls(host);
  const savedScroll = freezeHostScroll(host);
  host.classList.remove('is-shipping-sheet-open');
  if (sheet instanceof HTMLElement) {
    sheet.classList.remove('is-open');
    sheet.hidden = true;
  }
  if (backdrop instanceof HTMLElement) backdrop.hidden = true;
  restoreHostScroll(savedScroll);
}

/**
 * @param {HTMLElement} host
 * @param {string} cep
 */
async function estimateShipping(host, cep) {
  const { sheet, status, rates } = getSheetEls(host);
  if (!(sheet instanceof HTMLElement)) return;

  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) {
    if (rates instanceof HTMLElement) rates.replaceChildren();
    setStatus(
      status instanceof HTMLElement ? status : null,
      sheet.dataset.msgEmpty || '',
      digits.length > 0
    );
    return;
  }

  const seq = ++estimateSeq;
  setStatus(
    status instanceof HTMLElement ? status : null,
    sheet.dataset.msgLoading || 'Calculando…',
    true
  );
  if (rates instanceof HTMLElement) rates.replaceChildren();

  try {
    const cartTotalCents = getCartTotalCents(host);
    const freeThreshold = getFreeShipThresholdCents(host);

    // Sempre cotar carriers (Frenet). Expresso/Retirada entram via mergeRates
    // (Ajax Cart não devolve Local Delivery / pickup de forma confiável) — ADR-083.
    const geo = await lookupCepGeo(digits);
    if (seq !== estimateSeq) return;
    let rateList = await fetchShippingRates(digits, geo);
    if (seq !== estimateSeq) return;

    if (
      window.GMFreteExpresso &&
      typeof window.GMFreteExpresso.mergeRates === 'function'
    ) {
      rateList = window.GMFreteExpresso.mergeRates(
        rateList,
        digits,
        cartTotalCents,
        freeThreshold,
        { preVenda: host.getAttribute('data-gm-pre-venda') === 'true' }
      );
    }

    writeCepDigits(digits);
    const stored = readStoredRate();
    const selectedCode = stored?.code || null;
    renderRates(host, rateList, selectedCode);
  } catch (err) {
    console.error('[gm-cart-drawer] shipping', err);
    if (seq !== estimateSeq) return;
    if (rates instanceof HTMLElement) rates.replaceChildren();
    setStatus(status instanceof HTMLElement ? status : null, sheet.dataset.msgError || '', true);
  }
}

async function clearCart() {
  const clearUrl = `${Theme.routes.cart_url}/clear.js`.replace('/cart/clear.js', '/cart/clear.js');
  const res = await fetch(clearUrl, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('clear failed');

  let cart = null;
  try {
    cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
  } catch (_) {
    cart = { item_count: 0, items: [] };
  }

  try {
    sessionStorage.removeItem(STORAGE_CEP);
    sessionStorage.removeItem(STORAGE_RATE);
    sessionStorage.removeItem('gm_cart_cep_geo');
    writeCepDigits('');
  } catch (_) {
    /* ignore */
  }

  const component = document.querySelector('cart-items-component[data-drawer]');
  const template = document.getElementById('empty-cart-template');
  if (component && template instanceof HTMLTemplateElement) {
    component.replaceChildren(document.importNode(template.content, true));
  }

  const dialog = document.querySelector('.cart-drawer__dialog');
  dialog?.classList.add('cart-drawer--empty');

  document.dispatchEvent(
    new CartUpdateEvent(cart, 'gm-cart-clear', {
      source: 'gm-cart-drawer',
      itemCount: 0,
    })
  );
}

function hydrateAllShippingHosts() {
  document.querySelectorAll('.gm-cart-drawer__main, [data-gm-shipping-host]').forEach((el) => {
    if (el instanceof HTMLElement && el.querySelector('[data-gm-cart-shipping]')) {
      hydrateShippingSummary(el);
    }
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
function setCheckoutHidden(form, name, value) {
  let input = form.querySelector(`input[data-gm-checkout-prefill][name="${CSS.escape(name)}"]`);
  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.setAttribute('data-gm-checkout-prefill', '');
    form.appendChild(input);
  }
  input.value = value;
}

/**
 * @param {HTMLFormElement} form
 * @returns {Record<string, string> | null}
 */
function injectCheckoutShippingFields(form) {
  const GM = window.GMFreteExpresso;
  const cep = readCepDigits();
  if (cep.length !== 8) return null;
  const geo = GM && typeof GM.readCepGeo === 'function' ? GM.readCepGeo() : null;
  const params =
    GM && typeof GM.checkoutPrefillParams === 'function'
      ? GM.checkoutPrefillParams(cep, geo)
      : {
          'checkout[shipping_address][zip]': formatCep(cep),
          'checkout[shipping_address][country]': 'Brazil',
        };
  if (!params) return null;
  Object.keys(params).forEach((name) => setCheckoutHidden(form, name, params[name]));
  return params;
}

/**
 * @param {Record<string, string>} params
 * @returns {string}
 */
function checkoutPrefillQuery(params) {
  const qs = new URLSearchParams();
  Object.keys(params).forEach((key) => qs.set(key, params[key]));
  return qs.toString();
}

function checkoutPath() {
  const cartUrl =
    (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
  return String(cartUrl).replace(/\/cart(?:\.js)?\/?$/, '/checkout');
}

/**
 * Spec 0116: CEP da sacola no checkout nativo.
 * @param {Event} e
 */
function handleCheckoutPrefill(e) {
  const el =
    e.target instanceof Element
      ? e.target.closest('[name="checkout"], button.gm-cart-drawer__checkout, #checkout')
      : null;
  if (!el || (el instanceof HTMLButtonElement && el.disabled)) return;

  const form =
    (el instanceof HTMLButtonElement && el.form) ||
    (el instanceof HTMLInputElement && el.form) ||
    document.getElementById('cart-form');
  if (!(form instanceof HTMLFormElement)) return;

  const params = injectCheckoutShippingFields(form);
  if (!params) return;

  e.preventDefault();
  e.stopPropagation();
  window.location.assign(`${checkoutPath()}?${checkoutPrefillQuery(params)}`);
}

document.addEventListener('click', handleCheckoutPrefill, true);

document.addEventListener('gm:cep-change', () => {
  hydrateAllShippingHosts();
});

document.addEventListener('click', async (e) => {
  const clearBtn = e.target instanceof Element ? e.target.closest('[data-gm-cart-clear]') : null;
  if (clearBtn) {
    e.preventDefault();
    try {
      await clearCart();
    } catch (err) {
      console.error('[gm-cart-drawer] clear', err);
    }
    return;
  }

  const sheetClose = e.target instanceof Element ? e.target.closest('[data-gm-shipping-sheet-close]') : null;
  if (sheetClose) {
    e.preventDefault();
    const host = getShippingHost(sheetClose);
    if (host) closeShippingSheet(host);
    return;
  }

  const backdrop = e.target instanceof Element ? e.target.closest('[data-gm-shipping-sheet-backdrop]') : null;
  if (backdrop) {
    e.preventDefault();
    const host = getShippingHost(backdrop);
    if (host) closeShippingSheet(host);
    return;
  }

  const shippingSelected =
    e.target instanceof Element ? e.target.closest('[data-gm-cart-shipping-selected]') : null;
  if (shippingSelected) {
    e.preventDefault();
    const host = getShippingHost(shippingSelected);
    if (host) openShippingSheet(host);
  }
});

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-gm-cart-cep-form')) return;
  e.preventDefault();

  const host = getShippingHost(form);
  if (!host) return;

  const input = form.querySelector('[data-gm-cart-cep-input]');
  if (!(input instanceof HTMLInputElement)) return;

  input.value = formatCep(input.value);
  writeCepDigits(input.value.replace(/\D/g, ''));
  if (cepDebounceTimer) {
    clearTimeout(cepDebounceTimer);
    cepDebounceTimer = null;
  }
  openShippingSheet(host);
});

document.addEventListener('input', (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-gm-cart-cep-input')) return;
  input.value = formatCep(input.value);

  const host = getShippingHost(input);
  if (!host) return;

  const digits = input.value.replace(/\D/g, '');
  if (cepDebounceTimer) clearTimeout(cepDebounceTimer);

  if (digits.length !== 8) {
    if (digits.length === 0) writeCepDigits('');
    if (digits.length === 0 && host.classList.contains('is-shipping-sheet-open')) {
      closeShippingSheet(host);
    }
    return;
  }

  writeCepDigits(digits);

  cepDebounceTimer = setTimeout(() => {
    openShippingSheet(host);
  }, DEBOUNCE_MS);
});

document.addEventListener(
  'keydown',
  (e) => {
    if (e.key !== 'Escape') return;
    const openHost = document.querySelector('.is-shipping-sheet-open');
    if (!(openHost instanceof HTMLElement)) return;
    e.preventDefault();
    e.stopPropagation();
    closeShippingSheet(openHost);
  },
  true
);

document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (window.__gmShippingEstimate) return;
  requestAnimationFrame(() => {
    hydrateAllShippingHosts();
    setTimeout(() => hydrateAllShippingHosts(), 80);
  });
});

document.addEventListener('DOMContentLoaded', () => {
  hydrateAllShippingHosts();
});

hydrateAllShippingHosts();
